import React from 'react'
import Flatpickr from 'react-flatpickr'
import { useTranslation } from 'react-i18next'

import { addDays, addYears, isSameDay, min, parseISO } from 'date-fns'
import { utcToZonedTime } from 'date-fns-tz'
import { english } from 'flatpickr/dist/l10n/default'

import { MAX_NIGHTS, TIMEZONE } from '../../../constants'
import { useGameDays } from '../../../hooks'
import { getCurrentJstDate } from '../../../utils'

import 'flatpickr/dist/flatpickr.min.css'
import './flatpickr.scss'

// Directly set month labels to numbers
english.months.longhand[0] = '1'
english.months.longhand[1] = '2'
english.months.longhand[2] = '3'
english.months.longhand[3] = '4'
english.months.longhand[4] = '5'
english.months.longhand[5] = '6'
english.months.longhand[6] = '7'
english.months.longhand[7] = '8'
english.months.longhand[8] = '9'
english.months.longhand[9] = '10'
english.months.longhand[10] = '11'
english.months.longhand[11] = '12'

type CalendarProps = {
  onChange: (startDate: Date, endDate: Date) => void
  startDate?: Date
  endDate?: Date
  todayDate?: Date
  mode?: 'range' | 'single'
}

/**
 * Resolve the flatpickr `maxDate` while a range is being selected.
 *
 * Once a check-in is chosen (`selectedDates.length === 1`) the check-out is
 * capped at MAX_NIGHTS nights later, but never past the absolute upper bound
 * (`yearMax`, i.e. one year out). When the range is complete or cleared, the
 * bound is reset to `yearMax` so the next selection starts unconstrained.
 *
 * Note: this only greys out out-of-range cells during a *forward* selection. It
 * is a UX aid, not the source of truth — `clampRangeToMaxNights` enforces the
 * cap on the completed range (covering reverse picks and re-renders too).
 */
export const resolveRangeMaxDate = (
  selectedDates: Date[],
  yearMax: Date
): Date =>
  selectedDates.length === 1
    ? min([addDays(selectedDates[0], MAX_NIGHTS), yearMax])
    : yearMax

/**
 * Enforce the MAX_NIGHTS cap on a completed [checkIn, checkOut] range.
 *
 * flatpickr sorts the two clicks ascending, so a "reverse pick" (later date
 * first, earlier date second) still arrives here as [earlier, later]. If the
 * span exceeds MAX_NIGHTS the check-out is pulled back to exactly
 * `checkIn + MAX_NIGHTS`. Returns the input unchanged when it is already within
 * the cap (or not a complete pair), so callers can compare by reference.
 */
export const clampRangeToMaxNights = (selectedDates: Date[]): Date[] => {
  if (selectedDates.length !== 2) return selectedDates
  const [checkIn, checkOut] = selectedDates
  const cappedCheckOut = addDays(checkIn, MAX_NIGHTS)
  return checkOut > cappedCheckOut ? [checkIn, cappedCheckOut] : selectedDates
}

export const Calendar: React.FC<CalendarProps> = ({
  onChange,
  startDate,
  endDate,
  todayDate,
  mode = 'range',
}) => {
  const { i18n } = useTranslation()
  const { gameDays } = useGameDays()

  const jstTodayDate = todayDate
    ? utcToZonedTime(todayDate, TIMEZONE)
    : parseISO(getCurrentJstDate())
  const maxDate = addYears(jstTodayDate, 1)

  // Derive the rendered `maxDate` from the controlled props so the 99-night cap
  // survives the state-driven re-render that follows each selection (otherwise
  // react-flatpickr would reapply the uncapped 1-year value and undo the
  // imperative `fp.set` in onChange). While a check-in is chosen but the
  // check-out isn't, narrow it; otherwise fall back to the 1-year bound.
  const optionsMaxDate =
    mode === 'range' && startDate && !endDate
      ? resolveRangeMaxDate([startDate], maxDate)
      : maxDate

  return (
    <Flatpickr
      className={i18n.language === 'ja' ? 'languageJa' : ''}
      onChange={(dates, _dateStr, fp) => {
        if (mode !== 'range') {
          onChange(dates[0], dates[1])
          return
        }

        // Grey out out-of-range cells during a forward selection (UX aid).
        fp.set('maxDate', resolveRangeMaxDate(dates, maxDate))

        // Enforce the 99-night cap on the completed range — this is the source
        // of truth. It also covers reverse picks (later date clicked first),
        // which `maxDate` alone cannot block. If we clamped, push the corrected
        // selection back into flatpickr so the highlight matches the state we
        // emit; setDate(..., false) avoids re-triggering onChange.
        const capped = clampRangeToMaxNights(dates)
        if (capped !== dates) {
          fp.setDate(capped, false)
        }
        onChange(capped[0], capped[1])
      }}
      onDayCreate={(
        dObj,
        dStr,
        fp,
        dayElem: HTMLSpanElement & { dateObj: Date }
      ) => {
        const isSkipDay = [
          'nextMonthDay',
          'prevMonthDay',
          'flatpickr-disabled',
        ].some((skipClass) => dayElem.classList.contains(skipClass))
        const zonedDate = utcToZonedTime(dayElem.dateObj, TIMEZONE)
        const gameInfo = gameDays.find(({ date: gameDay }) =>
          isSameDay(gameDay, zonedDate)
        )

        // Build the day cell with direct DOM operations instead of a per-cell
        // React root. onDayCreate runs for every cell on every redraw, so
        // createRoot().render() here made selection slow (and leaked roots).
        // eslint-disable-next-line no-param-reassign
        dayElem.textContent = ''

        const wrapper = document.createElement('div')
        if (isSkipDay) wrapper.classList.add('skipDay')

        const day = document.createElement('div')
        day.className = 'day'
        day.textContent = String(dayElem.dateObj.getDate())
        wrapper.appendChild(day)

        const gameInfoEl = document.createElement('div')
        gameInfoEl.className = 'gameInfo'
        if (gameInfo?.image) {
          const img = document.createElement('img')
          img.className = 'teamLogo'
          img.src = gameInfo.image
          img.alt = 'Opponent logo'
          gameInfoEl.appendChild(img)
        }
        wrapper.appendChild(gameInfoEl)

        dayElem.appendChild(wrapper)
      }}
      options={{
        minDate: jstTodayDate,
        maxDate: optionsMaxDate,
        inline: true,
        mode,
        monthSelectorType: 'static',
        locale: english,
        defaultDate: !startDate || !endDate ? [] : [startDate, endDate],
        now: jstTodayDate,
      }}
    />
  )
}
